import React, { useEffect, useMemo, useState } from "react";
import { Clock, Send } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { presentMutationError } from "../../../utils/showApiErrorToast";
import { getDeploySetup, updateDeployment } from "../../../services/strategyDeployApi";
import type { Deployment, DeploySetup } from "../../../types/strategyDeploy";
import { ConnectTelegramButton } from "./ConnectTelegramButton";
import { MODE_LABEL } from "./liveStrategiesFormat";
import {
  liveBodyTextClassName,
  liveFieldLabelClassName,
  liveInsetClassName,
  liveOptionTitleClassName,
  liveSectionTitleClassName,
  liveSmallTextClassName,
} from "./liveStrategiesUi";
import { LiveBadge, LiveIconTile } from "./LiveUiParts";

export const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function num(v: string): number | null {
  const n = Number(v);
  return v.trim() !== "" && Number.isFinite(n) ? n : null;
}

const str = (v: number | null | undefined, fallback = ""): string => (v == null ? fallback : String(v));

export function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <Label className={liveFieldLabelClassName}>{label}</Label>
      {children}
      {hint ? <span className={cn(liveSmallTextClassName, "text-[11px]")}>{hint}</span> : null}
    </div>
  );
}

/** Position size: the app's pill tabs. */
export function SizingTabs({
  value,
  onChange,
}: {
  value: "risk_pct" | "fixed";
  onChange: (v: "risk_pct" | "fixed") => void;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className={liveFieldLabelClassName}>Position size</Label>
      <Tabs data-trade-log-tabs="" value={value} onValueChange={(v) => onChange(v as "risk_pct" | "fixed")}>
        <TabsList aria-label="Position size">
          <TabsTrigger value="risk_pct" data-live-sizing="risk_pct">
            % of the account at risk
          </TabsTrigger>
          <TabsTrigger value="fixed" data-live-sizing="fixed">
            Fixed size
          </TabsTrigger>
        </TabsList>
      </Tabs>
    </div>
  );
}

/** Trading days: rectangular app buttons (brand when on). */
export function DayPicker({ days, onChange }: { days: number[]; onChange: (d: number[]) => void }): JSX.Element {
  return (
    <div className="flex flex-wrap gap-1.5">
      {DAYS.map((d, i) => {
        const on = days.includes(i);
        return (
          <Button
            key={d}
            type="button"
            size="xs"
            variant={on ? "gradient" : "outline-gray"}
            data-font="primary"
            className="h-8 w-11 px-0 shadow-none"
            onClick={() => onChange(on ? days.filter((x) => x !== i) : [...days, i].sort())}
            aria-pressed={on}
          >
            <span className="normal-case tracking-normal">{d}</span>
          </Button>
        );
      })}
    </div>
  );
}

/** One setting row: brand icon tile, brand title, muted description, its control on the right. */
export function SettingRow({
  icon,
  title,
  badge,
  description,
  control,
  children,
  className,
}: {
  icon: React.ComponentProps<typeof LiveIconTile>["icon"];
  title: string;
  badge?: React.ReactNode;
  description: React.ReactNode;
  control: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
}): JSX.Element {
  return (
    <div className={cn("flex flex-col gap-4 p-5", className)}>
      <div className="flex items-center justify-between gap-4">
        <span className="flex min-w-0 items-start gap-3">
          <LiveIconTile icon={icon} />
          <span className="flex min-w-0 flex-col gap-1">
            <span className={cn(liveOptionTitleClassName, "flex flex-wrap items-center gap-2")}>
              {title}
              {badge}
            </span>
            <span className={liveSmallTextClassName}>{description}</span>
          </span>
        </span>
        <span className="shrink-0">{control}</span>
      </div>
      {children}
    </div>
  );
}

/**
 * Change a launched strategy's settings: name, size, limits, signal expiry, trading hours, Telegram,
 * paper balance. Mode and account stay as launched (open trades belong to them); "Run alongside"
 * tries another. Changes apply from the next signal; open trades keep their size, stop and target.
 *
 * The panel is hosted inside the deployment detail dialog (slid in beside the detail view);
 * `DeploymentSettingsDialog` wraps the same panel in its own dialog.
 */
export function DeploymentSettingsPanel({
  dep,
  active,
  onDone,
  onSaved,
  showHeader = true,
}: {
  dep: Deployment;
  /** True while the panel is showing: each showing starts from what is stored now. */
  active: boolean;
  /** Close / slide back (Cancel, or after a save). */
  onDone: () => void;
  onSaved: (dep: Deployment) => void;
  showHeader?: boolean;
}): JSX.Element {
  const open = active;
  const cfg = dep.config;
  const risk = cfg.risk;
  const browserTz = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC", []);
  const [name, setName] = useState(dep.name);
  const [sizing, setSizing] = useState<"risk_pct" | "fixed">(risk.sizing);
  const [riskPct, setRiskPct] = useState(str(risk.risk_pct, "0.5"));
  const [fixedQty, setFixedQty] = useState(str(risk.fixed_quantity, "0.1"));
  const [maxQty, setMaxQty] = useState(str(risk.max_quantity));
  const [dailyLoss, setDailyLoss] = useState(str(risk.max_daily_loss_pct, "3"));
  const [maxOpen, setMaxOpen] = useState(str(risk.max_open_trades, "1"));
  const [maxDay, setMaxDay] = useState(str(risk.max_trades_per_day, "3"));
  const [ttl, setTtl] = useState(str(cfg.signal_ttl_bars, "2"));
  const [telegram, setTelegram] = useState(Boolean(cfg.notify?.telegram));
  const [useHours, setUseHours] = useState(Boolean(cfg.schedule));
  const [days, setDays] = useState<number[]>(cfg.schedule?.days ?? [0, 1, 2, 3, 4]);
  const [start, setStart] = useState(cfg.schedule?.start ?? "07:00");
  const [end, setEnd] = useState(cfg.schedule?.end ?? "17:00");
  const tz = cfg.schedule?.tz ?? browserTz;
  const [paperEquity, setPaperEquity] = useState(str(cfg.paper?.equity, "10000"));
  const [setup, setSetup] = useState<DeploySetup | null>(null);
  const [busy, setBusy] = useState(false);
  const live = dep.mode !== "paper";

  // Each opening starts from what is stored now.
  useEffect(() => {
    if (!open) return;
    setName(dep.name);
    setSizing(risk.sizing);
    setRiskPct(str(risk.risk_pct, "0.5"));
    setFixedQty(str(risk.fixed_quantity, "0.1"));
    setMaxQty(str(risk.max_quantity));
    setDailyLoss(str(risk.max_daily_loss_pct, "3"));
    setMaxOpen(str(risk.max_open_trades, "1"));
    setMaxDay(str(risk.max_trades_per_day, "3"));
    setTtl(str(cfg.signal_ttl_bars, "2"));
    setTelegram(Boolean(cfg.notify?.telegram));
    setUseHours(Boolean(cfg.schedule));
    setDays(cfg.schedule?.days ?? [0, 1, 2, 3, 4]);
    setStart(cfg.schedule?.start ?? "07:00");
    setEnd(cfg.schedule?.end ?? "17:00");
    setPaperEquity(str(cfg.paper?.equity, "10000"));
    const controller = new AbortController();
    void getDeploySetup(controller.signal).then(setSetup).catch(() => setSetup(null));
    return () => controller.abort();
    // Re-read only on open / another deployment / a save elsewhere (dep.updated_at).
  }, [open, dep.id, dep.updated_at]);

  const error = ((): string | null => {
    if (!name.trim()) return "Give it a name.";
    if (sizing === "risk_pct" && !(num(riskPct)! > 0)) return "Risk per trade must be above 0%.";
    if (sizing === "risk_pct" && num(riskPct)! > (setup?.max_risk_pct ?? 5))
      return `Risk per trade is capped at ${setup?.max_risk_pct ?? 5}%.`;
    if (sizing === "fixed" && !(num(fixedQty)! > 0)) return "Enter a position size.";
    if (live && !(num(maxQty)! > 0)) return "Set a maximum position size.";
    if (!(num(dailyLoss)! > 0)) return "Daily loss limit must be above 0%.";
    if (!(num(maxOpen)! >= 1)) return "Max open trades must be at least 1.";
    if (!(num(maxDay)! >= 1)) return "Max trades a day must be at least 1.";
    if (useHours && !days.length) return "Pick at least one trading day.";
    return null;
  })();

  const save = async () => {
    if (error) {
      toast.error(error);
      return;
    }
    setBusy(true);
    try {
      const out = await updateDeployment(dep.id, {
        name: name.trim(),
        config: {
          risk: {
            sizing,
            risk_pct: sizing === "risk_pct" ? num(riskPct) : null,
            fixed_quantity: sizing === "fixed" ? num(fixedQty) : null,
            max_quantity: num(maxQty),
            max_daily_loss_pct: num(dailyLoss) ?? 3,
            max_open_trades: Math.floor(num(maxOpen) ?? 1),
            max_trades_per_day: Math.floor(num(maxDay) ?? 3),
          },
          schedule: useHours ? { days, start, end, tz } : null,
          signal_ttl_bars: Math.floor(num(ttl) ?? 2),
          notify: { telegram },
          ...(dep.mode === "paper" ? { paper: { equity: num(paperEquity) ?? 10000 } } : {}),
        },
      });
      toast.success("Settings saved", { description: "They apply from the next signal; open trades keep theirs." });
      onSaved(out);
      onDone();
    } catch (e) {
      presentMutationError(e, "Could not save the settings");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-w-0 flex-col gap-4" data-live-deployment-settings="">
      {showHeader ? (
        <div className="flex flex-col gap-1">
          <h3 className={liveSectionTitleClassName}>Settings: {dep.name}</h3>
          <p className={cn(liveBodyTextClassName, "m-0")}>
            Changes apply from the next signal. Open trades keep their size, stop and target. Mode ({MODE_LABEL[dep.mode]})
            and account stay as launched; use Run alongside to try another.
          </p>
        </div>
      ) : null}

      <Field label="Name">
        <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />
      </Field>

      <SizingTabs value={sizing} onChange={setSizing} />

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        {sizing === "risk_pct" ? (
          <Field label="Risk per trade (%)" hint={`Max ${setup?.max_risk_pct ?? 5}%`}>
            <Input value={riskPct} onChange={(e) => setRiskPct(e.target.value)} inputMode="decimal" />
          </Field>
        ) : (
          <Field label="Position size (lots)">
            <Input value={fixedQty} onChange={(e) => setFixedQty(e.target.value)} inputMode="decimal" />
          </Field>
        )}
        <Field label="Never larger than (lots)" hint={live ? "Required" : "Optional on paper"}>
          <Input value={maxQty} onChange={(e) => setMaxQty(e.target.value)} inputMode="decimal" />
        </Field>
        <Field label="Daily loss limit (%)">
          <Input value={dailyLoss} onChange={(e) => setDailyLoss(e.target.value)} inputMode="decimal" />
        </Field>
        <Field label="Max open trades">
          <Input value={maxOpen} onChange={(e) => setMaxOpen(e.target.value)} inputMode="numeric" />
        </Field>
        <Field label="Max trades a day">
          <Input value={maxDay} onChange={(e) => setMaxDay(e.target.value)} inputMode="numeric" />
        </Field>
        {dep.mode === "approve" ? (
          <Field label="Signal expires after (candles)">
            <Input value={ttl} onChange={(e) => setTtl(e.target.value)} inputMode="numeric" />
          </Field>
        ) : null}
        {dep.mode === "paper" ? (
          <Field label="Paper account ($)">
            <Input value={paperEquity} onChange={(e) => setPaperEquity(e.target.value)} inputMode="decimal" />
          </Field>
        ) : null}
      </div>

      <div className={liveInsetClassName}>
        <SettingRow
          icon={Clock}
          title="Trading hours"
          description={`Only take new trades inside these hours (${tz}).`}
          control={<Switch checked={useHours} onCheckedChange={setUseHours} aria-label="Trading hours" />}
        >
          {useHours ? (
            <div className="flex flex-wrap items-center gap-3">
              <DayPicker days={days} onChange={setDays} />
              <Input type="time" value={start} onChange={(e) => setStart(e.target.value)} className="w-28" />
              <span className={liveSmallTextClassName}>to</span>
              <Input type="time" value={end} onChange={(e) => setEnd(e.target.value)} className="w-28" />
            </div>
          ) : null}
        </SettingRow>
        <SettingRow
          className="!border-0 !border-t !border-solid !border-border"
          icon={Send}
          title="Telegram"
          badge={
            setup == null ? null : (
              <LiveBadge tone={setup.telegram_linked ? "good" : "warn"}>
                {setup.telegram_linked ? "Connected" : "Not connected"}
              </LiveBadge>
            )
          }
          description={dep.mode === "approve" ? "Signals arrive with Approve and Reject buttons." : "Get every signal, fill and close."}
          control={
            setup && !setup.telegram_linked ? (
              <ConnectTelegramButton
                onReturn={() => void getDeploySetup().then(setSetup).catch(() => undefined)}
                size="sm"
                variant="gradient"
                label="Connect"
              />
            ) : (
              <Switch checked={telegram} onCheckedChange={setTelegram} aria-label="Send to Telegram" />
            )
          }
        />
      </div>

      <div className="flex items-center justify-end gap-2 !border-0 !border-t !border-solid !border-border pt-4">
        <Button type="button" variant="outline-brand" size="sm" data-font="primary" className="h-8 px-4 shadow-none" onClick={onDone}>
          <span className="normal-case tracking-normal">Cancel</span>
        </Button>
        <Button
          type="button"
          variant="gradient"
          size="sm"
          data-font="primary"
          className="h-8 px-4 shadow-none"
          disabled={busy}
          onClick={() => void save()}
          data-live-settings-save=""
        >
          <span className="normal-case tracking-normal">{busy ? "Saving…" : "Save settings"}</span>
        </Button>
      </div>
    </div>
  );
}

/** The settings panel in its own dialog (for surfaces without the detail dialog). */
export function DeploymentSettingsDialog({
  dep,
  open,
  onOpenChange,
  onSaved,
}: {
  dep: Deployment;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: (dep: Deployment) => void;
}): JSX.Element {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl" data-live-ui="">
        <DialogHeader>
          <DialogTitle className={liveSectionTitleClassName}>Settings: {dep.name}</DialogTitle>
          <DialogDescription className={liveBodyTextClassName}>
            Changes apply from the next signal. Open trades keep their size, stop and target.
          </DialogDescription>
        </DialogHeader>
        <DeploymentSettingsPanel dep={dep} active={open} onDone={() => onOpenChange(false)} onSaved={onSaved} showHeader={false} />
      </DialogContent>
    </Dialog>
  );
}
